import { z } from 'zod';

/**
 * Local stand-ins until `@air/server-sdk` is synced (WORK-937; backend
 * [AirLabsTeam/backend#9445](https://github.com/AirLabsTeam/backend/pull/9445), migration #9444).
 *
 * @TODO(WORK-937): After SDK sync, delete this module's type aliases and Zod parsers where the
 * published types suffice. Backend `packages/server-sdk/src/resources/Agents/types.ts` exports:
 * - `AgentTableColumn`, `AgentTableCell`, `AgentTableRow`
 * - `TableArtifactSummary` on `SessionArtifact` (`type: 'table'`)
 * - `ListAgentSessionArtifactRowsResponse` (`type: 'table'`; row `data[].id`, `position`, `payload`)
 * Keep runtime parsing only if we still need to guard pre-backend deploy responses.
 */
const WorkspaceAgentTableColumnKindSchema = z.enum([
  'text',
  'number',
  'boolean',
  'timestamp',
  'url',
  'json',
  'list',
  'ref',
]);

export type WorkspaceAgentTableColumnKind = z.infer<typeof WorkspaceAgentTableColumnKindSchema>;

export type WorkspaceAgentTableColumn = {
  key: string;
  kind?: WorkspaceAgentTableColumnKind;
  label: string;
};

export type WorkspaceAgentTableCell = string | number | boolean | null;

export type WorkspaceAgentTableRow = Record<string, WorkspaceAgentTableCell>;

export type WorkspaceAgentTableArtifactSummary = {
  columns: WorkspaceAgentTableColumn[];
  count?: number;
  title?: string;
  truncated?: boolean;
};

export type WorkspaceAgentTableRowsPage = {
  data: { payload: WorkspaceAgentTableRow; position: number }[];
  pagination: { hasMore: boolean };
  type: 'table';
};

const WorkspaceAgentTableColumnSchema: z.ZodType<WorkspaceAgentTableColumn> = z.object({
  key: z.string().min(1),
  kind: WorkspaceAgentTableColumnKindSchema.optional(),
  label: z.string(),
});

const TableCellSchema: z.ZodType<WorkspaceAgentTableCell> = z.union([z.string(), z.number(), z.boolean(), z.null()]);

const TableRowSchema: z.ZodType<WorkspaceAgentTableRow> = z.record(z.string(), TableCellSchema);

const TableArtifactSummarySchema: z.ZodType<WorkspaceAgentTableArtifactSummary> = z.object({
  columns: z.array(WorkspaceAgentTableColumnSchema),
  count: z.number().int().min(0).optional(),
  title: z.string().optional(),
  truncated: z.boolean().optional(),
});

const TableArtifactSchema = z.object({
  summary: TableArtifactSummarySchema,
  type: z.literal('table'),
});

// @TODO(WORK-937): Align with SDK row shape (`id` + `position` + `payload`) after #9445 SDK sync.
const TableRowsPageSchema: z.ZodType<WorkspaceAgentTableRowsPage> = z.object({
  data: z.array(z.object({ payload: TableRowSchema, position: z.number() })),
  pagination: z.object({ hasMore: z.boolean() }),
  type: z.literal('table'),
});

export const parseWorkspaceAgentTableArtifactSummary = (
  artifact: unknown,
): WorkspaceAgentTableArtifactSummary | undefined => {
  const parsed = TableArtifactSchema.safeParse(artifact);

  return parsed.success ? parsed.data.summary : undefined;
};

export const parseWorkspaceAgentTableRowsPage = (page: unknown): WorkspaceAgentTableRowsPage | undefined => {
  const parsed = TableRowsPageSchema.safeParse(page);

  return parsed.success ? parsed.data : undefined;
};
